import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, Link } from 'react-router-dom';

import Chip from '@mui/joy/Chip';
import useClock from 'hooks/useClock';
import useUserIdentity from 'hooks/useUserIdentity';
import useCollabs from 'hooks/useCollabs';
import useAuth from 'hooks/useAuth';
// import useRoles from 'hooks/useRoles';

import Button from '@mui/joy/Button';
import Stack from '@mui/joy/Stack';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Tooltip from '@mui/joy/Tooltip';
import Modal from  '@mui/joy/Modal';
import Card  from '@mui/joy/Card';


import { getIcon } from 'utils/misc';
import { Duration } from './Date';
import useRating from 'hooks/useRating';

import IdentityAvatar from 'components/IdentityAvatar';
import RatingCardThumbs from "components/RatingCardThumbs";
import { Success, Error } from 'components/FancyToast';
import { ClockProps } from 'stores/clock_store';
import { RequestChunk } from 'components/ProjectHeader';
import { CollabChunk } from 'components/ProjectHeader';

import useFHRequest from 'hooks/useFHRequest';
import { FHRequestType } from 'stores/fhrequest_store';

export const ClockOutModalRequest = observer(({setOpen, open, clock}:{setOpen: (k: boolean) => any, open: boolean, clock: ClockProps}) => {
    const { getRequest } = useFHRequest();
    const { currentIdentity } = useUserIdentity();

    const { rateItem } = useRating();
    const { clockOut } = useClock();
    const [score, setScore] = useState(0);

    const request = getRequest(clock.associated_uuid);

    useEffect(() => {
        setScore(0);
    }, [clock.key]);

    function doClockOut(clock: ClockProps) {
        clockOut(clock.key);

        if (request?.identity && currentIdentity && score !== 0) {
            rateItem(clock.key, score, currentIdentity?.key, request?.identity)
                .then(() => {
                    Success("Rated this!");
                    setOpen(false);
                    //navigate(`request/${clock.associated_uuid}`);
                }).
                catch((e) => {
                    console.error(e);
                    Error("Failed to rate this");
                });
        }
        else {
            Success('Clocked out');
            setOpen(false);
            //navigate(window.location.href);
        }
    }

    return (
        <Modal
            open={open}
            onClose={() => setOpen(false)}
            sx={{ display: 'flex',
                justifyContent: 'center',
                alignItems: 'center' }}
        >
            <Card>

                <Typography level="title-md">
                    Clock out of this request?
                </Typography>

                <Stack spacing={3} my={1}>

                    {request?.identity &&
                        <RatingCardThumbs
                            label={<Typography level="title-sm">
                                Rate this collaboration with
                                <IdentityAvatar
                                    size="chip"
                                    interactive={false}
                                    identityKey={request?.identity} />
                            </Typography>}
                            setScore={setScore}
                            score={score} />}

                    {!request?.identity &&
                        <Stack>
                        <Typography level="title-sm">
                            We cannot rate this request just yet.
                        </Typography>
                            <Typography level="body-xs">
                                They haven't set up an account on day ones.
                            </Typography>
                        </Stack>
                    }

                </Stack>

                <Button onClick={() => doClockOut(clock)} >
                    Clock out of this request
                </Button>

            </Card>

        </Modal>
    );
});


const ClockHeaderCollab = observer(({ collabKey }: { collabKey: string }) => {
    const { getCollab, fetchCollab, collabs } = useCollabs();
    const { currentIdentity } = useUserIdentity();
    const [collab, setCollab] = useState(getCollab(collabKey));
    const { clockIn, currentClock } = useClock();

    useEffect(() => {
        if(collab) return;
        fetchCollab(currentIdentity?.uuid, collabKey);
    }, [collab, collabKey, currentIdentity, fetchCollab]);

    useEffect(() => {
        if(collab && collab.uuid === collabKey) {
            return;
        }
        const c = getCollab(collabKey);
        setCollab(c);
    }, [collabs.length, getCollab, collabKey]);

    const ClockInIcon = getIcon('PunchClock');

    function doClockIn() {
        if (currentIdentity && collabKey) {
            clockIn(collabKey, '')
                .catch(() =>
                    Error("Unable to clock in on this collab!")
                )
        }
    }



    if(currentIdentity?.role != 'assistant') {
        return null;
    }

    if (!collab) {
        return null;
    }

    if(currentClock?.associated_uuid) {
        return <CollabChunk collab={collab} />;
    }

    return (
        <Stack
            spacing={1}
            alignItems="center"
            direction="row"
            width="100%"
            justifyContent="space-between"
        >
            <CollabChunk collab={collab} />
            <Tooltip
                arrow
                title={
                    (collab?.title ?
                        "Clock in to: " + collab?.title :
                        "Clock in to this collab")
                }
            >
                <Button
                    startDecorator={<ClockInIcon />}
                    onClick={doClockIn}>Clock In</Button>
            </Tooltip>
        </Stack>
    );
});

export const ClockInButton = observer(({request, variant=undefined}: {request: FHRequestType, variant?: "outlined"|"plain"|undefined}) => {
    const ClockInIcon = getIcon('PunchClock');
    const { clockIn } = useClock();
    const { currentIdentity } = useUserIdentity();

    function doClockInForRequest() {
        // this is request.key because some of old requests don't have uuids at this point
        if (currentIdentity && request.key) {
            clockIn('', request.key)
                .catch(() =>
                    Error("Unable to clock in on this collab!")
                )
        }
        else {
            console.error("No identity or request found for clock in", currentIdentity, request);
        }
    }

    if(currentIdentity?.role != 'assistant') {
        return null;
    }

    return (
        <Stack>
            <Tooltip
                arrow
                title={
                    (request?.title ?
                        "Clock in to: " + request?.title :
                        "Clock in to this request")
                }
            >
                <Button
                    variant={variant}
                    startDecorator={<ClockInIcon />}
                    onClick={doClockInForRequest}>
                    Clock In
                </Button>
            </Tooltip>
        </Stack>
    );


});

const ClockHeaderRequest = observer(({ requestKey }: { requestKey: string }) => {
    const { getRequest, hasPermission, fetchRequest } = useFHRequest();
    const { currentIdentity } = useUserIdentity();
    const { currentUser } = useAuth();
    const { currentClock } = useClock();


    const c = getRequest(requestKey);
    useEffect(() => {
        if(c) {
            return;
        }
        fetchRequest(requestKey)
    }, [c]);



    if (!c) {
        console.log("No request found for %o", requestKey);
        return null;
    }

    if(!hasPermission(c, currentUser.key, currentIdentity.uuid)) {
        return <RequestChunk request={c} />;

    }

    if(currentClock?.associated_uuid && c) {
        return <RequestChunk request={c} />;
    }

    return (
        <Stack
            spacing={1}
            alignItems="center"
            width="100%"

            justifyContent="space-between"
            direction="row">

            <RequestChunk request={c} />

            <ClockInButton request={c} />

        </Stack>
    );
});

const ClockHeader = observer(() => {
    const { collabKey, requestKey } = useParams<{ collabKey: string, requestKey: string }>();

    const { currentClock, clockOut } = useClock();

    const ClockOutIcon = getIcon('PunchClockOutlined');

    function doClockOut() {
        if (currentClock?.key) {
            clockOut(currentClock.key);
        }
    }

    const clockedInHere = (currentClock?.associated_uuid &&
        [requestKey, collabKey].includes(currentClock?.associated_uuid)
    );
    const clockedInHereStr = (clockedInHere ?
        "You are clocked in here" :
        "You are clocked in")

    const InHereIcon = (clockedInHere ? getIcon('Check') : getIcon('KeyboardArrowRight'));


        return (
            <>
                <Stack
                    width="100%"
                    py={1}
                    spacing={1}
                    flexGrow={1}
                  className="no-print"
                    alignItems="center"
                    direction="row">



                        { collabKey &&
                            <ClockHeaderCollab collabKey={collabKey}  />
                            }

                        { requestKey &&
                            <ClockHeaderRequest requestKey={requestKey} />
                        }





                    <Box flexGrow={1} />


                    { currentClock &&
                        <Stack>


                    <Tooltip arrow title={"Click to clock out of current collab."}>
                        <Button
                            size="sm"
                            variant="outlined"
                            startDecorator={<ClockOutIcon />}
                            onClick={doClockOut}>
                            Clock Out
                            <Stack direction="row" pl={1}>
                                <Duration level="body-xs"
                                    start={currentClock.time_start}
                                    end={currentClock.time_end} />
                            </Stack>
                        </Button>
                    </Tooltip>

                    <Stack direction="row" spacing={1} alignItems="center">
                        <Tooltip
                            arrow
                            title={clockedInHere ?
                                "You are clocked in here, you should continue working" :
                                "You are clocked in on a collab, you should get back to it or clock out"}
                        >
                            <Link to={`/${currentClock.associated_type}/${currentClock.associated_uuid}`}>

                            <Chip size="sm"
                                sx={{
                                    // marginLeft: -1, // calc(-1 * var(--ChipRadius)) ',
                                    transition: "all 0.5s ease-in-out"
                                }}
                                endDecorator={<InHereIcon />}
                                variant={clockedInHere ? "outlined" : "soft"}
                                color={clockedInHere ? "success" : "danger"}>
                                {clockedInHereStr}
                            </Chip>
                            </Link>
                        </Tooltip>

                    </Stack>

                        </Stack>

                    }

                </Stack>

            </>
        );

});
export default ClockHeader;
